/* eslint-disable react/no-unescaped-entities */
/**
 * ViralPost admin tool — internal social-media engine for Central Rentals.
 *
 * Tabs:
 *   • Queue        — generate + manage weekly posts (with optional A/B variants)
 *   • Schedule     — platform-specific best-time-to-post hints
 *   • Reciprocity  — daily rotated list of Canadian accounts to engage with
 *   • Reply asst   — paste a comment, get 3 brand-safe reply variants
 *
 * Gated by ADMIN_CONFIG_TOKEN (stashed in localStorage after unlock).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Sparkles, RefreshCw, Check, X, Copy, Loader2, Lock, Trash2, ImageOff,
  Calendar, Users, MessageCircle, Shuffle, ExternalLink, Rocket, Share2,
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const TOKEN_KEY = "cr_admin_token";

const PLATFORMS = [
  { key: "linkedin",  label: "LinkedIn" },
  { key: "x",         label: "X" },
  { key: "instagram", label: "Instagram" },
  { key: "facebook",  label: "Facebook" },
  { key: "tiktok",    label: "TikTok" },
];

const TOPIC_LABEL = {
  market_update:   "Market update",
  landlord_tip:    "Landlord tip",
  law_change:      "Law change",
  product_update:  "Product update",
  myth_bust:       "Myth bust",
  tenant_story:    "Tenant story",
  data_drop:       "Data drop",
  seasonal:        "Seasonal",
};

function cn(...c) { return c.filter(Boolean).join(" "); }

async function api(path, { token, method = "GET", body } = {}) {
  const r = await fetch(`${API}/api/viralpost${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "X-Admin-Token": token,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401) throw new Error("unauthorized");
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail || `${method} ${path} failed (${r.status})`);
  }
  return r.json();
}

// ---------------------------------------------------------------------------
// Lock screen
// ---------------------------------------------------------------------------
function LockScreen({ onUnlock }) {
  const [value, setValue] = useState("");
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-5">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <Lock className="w-5 h-5 text-emerald-700"/>
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">ViralPost</h1>
            <p className="text-sm text-slate-500">Internal team only</p>
          </div>
        </div>
        <div className="mt-6 space-y-3">
          <label className="text-sm font-semibold text-slate-700">Admin token</label>
          <input
            type="password"
            data-testid="viralpost-token-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="crc-cfg-…"
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-mono focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
          />
          <button
            data-testid="viralpost-unlock-btn"
            onClick={() => value.trim() && onUnlock(value.trim())}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
          >
            Unlock
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post card — image + platform tabs + A/B toggle + actions
// ---------------------------------------------------------------------------
function PostCard({ post, token, onChange }) {
  const [tab, setTab] = useState("linkedin");
  const [variant, setVariant] = useState("a");
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);

  const hasB = !!post.platforms_b;

  async function call(action) {
    setBusy(action);
    try {
      const path = action === "delete"
        ? `/queue/${post.id}`
        : `/queue/${post.id}/${action}`;
      await api(path, { token, method: action === "delete" ? "DELETE" : "POST" });
      await onChange();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  }

  const activeText = variant === "b" && hasB
    ? (post.platforms_b?.[tab] || "")
    : (post.platforms?.[tab] || "");

  function copy() {
    if (!activeText) return;
    navigator.clipboard.writeText(activeText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  const sched = post.schedule_hints?.[tab];

  const statusColors = {
    pending:  "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-slate-100 text-slate-500 border-slate-200",
  };

  return (
    <div
      data-testid={`viralpost-card-${post.id}`}
      className={cn(
        "bg-white border rounded-2xl overflow-hidden shadow-sm",
        post.status === "rejected" ? "border-slate-200 opacity-60" : "border-slate-200"
      )}
    >
      <div className="grid md:grid-cols-[260px_1fr] gap-0">
        {/* Image */}
        <div className="aspect-square md:aspect-auto bg-slate-100 flex items-center justify-center overflow-hidden">
          {post.image_data_uri
            ? <img src={post.image_data_uri} alt={post.image_prompt || "Generated"} className="w-full h-full object-cover"/>
            : <div className="text-slate-400 flex flex-col items-center gap-2 p-6 text-center">
                <ImageOff className="w-7 h-7"/>
                <span className="text-xs">Image gen failed — copy still ok</span>
              </div>
          }
        </div>

        {/* Right column */}
        <div className="p-5 flex flex-col">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-700">
                {TOPIC_LABEL[post.topic_key] || post.topic_key}
              </span>
              <span className={cn(
                "text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-full border",
                statusColors[post.status] || statusColors.pending,
              )}>
                {post.status}
              </span>
              {hasB && (
                <div className="ml-2 inline-flex rounded-full bg-slate-100 p-0.5" data-testid={`viralpost-variant-toggle-${post.id}`}>
                  {["a", "b"].map((v) => (
                    <button
                      key={v}
                      onClick={() => setVariant(v)}
                      className={cn(
                        "px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-bold rounded-full transition-colors",
                        variant === v ? "bg-white shadow text-slate-900" : "text-slate-500"
                      )}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                data-testid={`viralpost-approve-${post.id}`}
                disabled={busy !== null || post.status === "approved"}
                onClick={() => call("approve")}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 disabled:opacity-50"
              >
                {busy === "approve" ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Check className="w-3.5 h-3.5"/>}
                Approve
              </button>
              <button
                data-testid={`viralpost-reject-${post.id}`}
                disabled={busy !== null || post.status === "rejected"}
                onClick={() => call("reject")}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 disabled:opacity-50"
              >
                {busy === "reject" ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <X className="w-3.5 h-3.5"/>}
                Reject
              </button>
              <button
                data-testid={`viralpost-regen-${post.id}`}
                disabled={busy !== null}
                onClick={() => call("regenerate")}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 disabled:opacity-50"
                title="Regenerate copy + image"
              >
                {busy === "regenerate" ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <RefreshCw className="w-3.5 h-3.5"/>}
                Re-roll
              </button>
              <button
                data-testid={`viralpost-delete-${post.id}`}
                disabled={busy !== null}
                onClick={() => call("delete")}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-semibold rounded-lg hover:bg-rose-50 text-rose-600 border border-rose-200 disabled:opacity-50"
                title="Delete"
              >
                {busy === "delete" ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Trash2 className="w-3.5 h-3.5"/>}
              </button>
            </div>
          </div>

          {/* Platform tabs */}
          <div className="mt-4 flex items-center gap-1 border-b border-slate-200">
            {PLATFORMS.map((p) => (
              <button
                key={p.key}
                data-testid={`viralpost-tab-${post.id}-${p.key}`}
                onClick={() => setTab(p.key)}
                className={cn(
                  "px-3 py-1.5 text-xs font-semibold transition-colors -mb-px border-b-2",
                  tab === p.key
                    ? "border-emerald-600 text-emerald-700"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                )}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Schedule hint chip */}
          {sched && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-slate-500" data-testid={`viralpost-schedule-${post.id}`}>
              <Calendar className="w-3.5 h-3.5"/>
              <span>Best: <b className="text-slate-700">{sched.best_days.join("/")} · {sched.best_times.join(" · ")}</b> EST</span>
            </div>
          )}

          {/* Post copy */}
          <div className="mt-2 flex-1">
            <pre
              data-testid={`viralpost-copy-${post.id}-${tab}`}
              className="whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed bg-slate-50 border border-slate-200 rounded-lg p-3 min-h-[120px] max-h-[260px] overflow-y-auto"
            >{activeText || "(empty)"}</pre>
            <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-500">
              <span>{activeText.length} chars{hasB && ` · Variant ${variant.toUpperCase()}`}</span>
              <button
                data-testid={`viralpost-copy-btn-${post.id}-${tab}`}
                onClick={copy}
                className={cn(
                  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md font-semibold transition-colors",
                  copied
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                )}
              >
                <Copy className="w-3.5 h-3.5"/>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QUEUE tab
// ---------------------------------------------------------------------------
function QueueTab({ token, items, loading, generating, variantB, setVariantB, onGenerate, onChange, filter, setFilter, counts, error }) {
  const filtered = useMemo(() => (
    filter === "all" ? items : items.filter((p) => p.status === filter)
  ), [items, filter]);

  // Launch announcement modal — captures a single shipping note and
  // POSTs to /queue/launch which generates a tuned launch post.
  const [launchOpen, setLaunchOpen] = useState(false);
  const [launchText, setLaunchText] = useState("");
  const [launching, setLaunching] = useState(false);
  async function launch() {
    if (!launchText.trim() || launching) return;
    setLaunching(true);
    try {
      const r = await fetch(`${API}/api/viralpost/queue/launch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "X-Admin-Token": token },
        body: JSON.stringify({ shipped: launchText.trim() }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `Failed (${r.status})`);
      }
      setLaunchText("");
      setLaunchOpen(false);
      await onChange();
    } catch (e) {
      alert(e.message);
    } finally {
      setLaunching(false);
    }
  }

  // Cross-promote modal — amplify an existing post (e.g. a Facebook reel)
  // from the other 4 platforms.
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoUrl, setPromoUrl] = useState("");
  const [promoDesc, setPromoDesc] = useState("");
  const [promoCta, setPromoCta] = useState("watch + share with a fellow landlord");
  const [promoting, setPromoting] = useState(false);
  async function crossPromote() {
    if (!promoUrl.trim() || promoting) return;
    setPromoting(true);
    try {
      const r = await fetch(`${API}/api/viralpost/queue/cross-promote`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "X-Admin-Token": token },
        body: JSON.stringify({ url: promoUrl.trim(), description: promoDesc.trim(), cta: promoCta.trim() }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `Failed (${r.status})`);
      }
      setPromoUrl(""); setPromoDesc(""); setPromoOpen(false);
      await onChange();
    } catch (e) {
      alert(e.message);
    } finally {
      setPromoting(false);
    }
  }

  return (
    <>
      <div className="flex items-start justify-between gap-5 flex-wrap">
        <p className="text-slate-600 max-w-2xl">
          Generate a week of LinkedIn / X / Instagram / Facebook / TikTok posts in one click. Approve the winners and copy-paste into each platform — image included.
        </p>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={variantB}
              onChange={(e) => setVariantB(e.target.checked)}
              data-testid="viralpost-variantb-checkbox"
              className="rounded"
            />
            Include A/B hooks
          </label>
          <button
            data-testid="viralpost-launch-btn"
            onClick={() => setLaunchOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors shadow-sm"
            title="Announce a feature launch"
          >
            <Rocket className="w-4 h-4"/> Launch post
          </button>
          <button
            data-testid="viralpost-promo-btn"
            onClick={() => setPromoOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm transition-colors shadow-sm"
            title="Amplify an existing post from the other platforms"
          >
            <Share2 className="w-4 h-4"/> Cross-promote
          </button>
          <button
            data-testid="viralpost-generate-btn"
            onClick={onGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm transition-colors disabled:opacity-60 shadow-sm"
          >
            {generating
              ? <><Loader2 className="w-4 h-4 animate-spin"/> Generating 5 posts…</>
              : <><Sparkles className="w-4 h-4"/> Generate this week's queue</>}
          </button>
        </div>
      </div>

      {/* Launch modal */}
      {launchOpen && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-5" data-testid="viralpost-launch-modal" onClick={() => !launching && setLaunchOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <Rocket className="w-5 h-5 text-emerald-700"/>
              <h2 className="font-display text-xl font-bold text-slate-900">Announce a launch</h2>
            </div>
            <p className="mt-2 text-sm text-slate-600">One line about what shipped — smart engine writes a 5-platform launch announcement (with image) and drops it into your queue.</p>
            <textarea
              data-testid="viralpost-launch-input"
              value={launchText}
              onChange={(e) => setLaunchText(e.target.value)}
              rows={3}
              autoFocus
              placeholder="e.g. Smart Listing Studio — virtual staging + spec sheets in 60 seconds"
              className="mt-4 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 resize-none"
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={() => setLaunchOpen(false)} disabled={launching} className="px-3 py-2 text-sm font-semibold rounded-lg text-slate-600 hover:text-slate-900">Cancel</button>
              <button
                data-testid="viralpost-launch-submit"
                onClick={launch}
                disabled={launching || !launchText.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm rounded-lg disabled:opacity-60"
              >
                {launching ? <><Loader2 className="w-4 h-4 animate-spin"/> Drafting…</> : <><Rocket className="w-4 h-4"/> Generate launch post</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cross-promote modal */}
      {promoOpen && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-5" data-testid="viralpost-promo-modal" onClick={() => !promoting && setPromoOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-sky-700"/>
              <h2 className="font-display text-xl font-bold text-slate-900">Cross-promote a post</h2>
            </div>
            <p className="mt-2 text-sm text-slate-600">Paste a URL (your YouTube video, FB reel, IG post…) — smart engine generates 4 amplification posts on the other platforms that drive traffic + engagement to it.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Post URL</label>
                <input
                  data-testid="viralpost-promo-url"
                  value={promoUrl}
                  onChange={(e) => setPromoUrl(e.target.value)}
                  placeholder="https://www.facebook.com/share/r/…"
                  className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm font-mono outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">What it's about (1 sentence)</label>
                <input
                  data-testid="viralpost-promo-desc"
                  value={promoDesc}
                  onChange={(e) => setPromoDesc(e.target.value)}
                  placeholder="e.g. Reel showing smart maintenance triage in action"
                  className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Engagement CTA</label>
                <input
                  data-testid="viralpost-promo-cta"
                  value={promoCta}
                  onChange={(e) => setPromoCta(e.target.value)}
                  className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={() => setPromoOpen(false)} disabled={promoting} className="px-3 py-2 text-sm font-semibold rounded-lg text-slate-600 hover:text-slate-900">Cancel</button>
              <button
                data-testid="viralpost-promo-submit"
                onClick={crossPromote}
                disabled={promoting || !promoUrl.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm rounded-lg disabled:opacity-60"
              >
                {promoting ? <><Loader2 className="w-4 h-4 animate-spin"/> Drafting…</> : <><Share2 className="w-4 h-4"/> Generate amplification posts</>}
              </button>
            </div>
          </div>
        </div>
      )}


      <div className="mt-7 flex items-center gap-2 flex-wrap">
        {[
          ["all",      "All",      counts.all],
          ["pending",  "Pending",  counts.pending],
          ["approved", "Approved", counts.approved],
          ["rejected", "Rejected", counts.rejected],
        ].map(([k, label, n]) => (
          <button
            key={k}
            data-testid={`viralpost-filter-${k}`}
            onClick={() => setFilter(k)}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors",
              filter === k
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 hover:text-slate-900 border-slate-200"
            )}
          >
            {label} <span className="opacity-60">· {n}</span>
          </button>
        ))}
      </div>

      {error && (
        <div className="mt-4 px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg" data-testid="viralpost-error">{error}</div>
      )}

      <div className="mt-6 space-y-5">
        {loading && items.length === 0 && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin"/> Loading…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center" data-testid="viralpost-empty">
            <Sparkles className="w-6 h-6 text-slate-300 mx-auto"/>
            <p className="mt-2 text-slate-500 text-sm">
              {items.length === 0
                ? "No posts yet. Click 'Generate this week's queue' to start."
                : `No ${filter} posts in the queue.`}
            </p>
          </div>
        )}
        {filtered.map((p) => (
          <PostCard key={p.id} post={p} token={token} onChange={onChange} />
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// SCHEDULE tab — render the platform-specific best-times grid
// ---------------------------------------------------------------------------
function ScheduleTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { setData(await api("/schedule", { token })); } catch { /* no-op */ }
      finally { setLoading(false); }
    })();
  }, [token]);
  if (loading) return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading schedule…</div>;
  if (!data) return null;
  return (
    <div data-testid="viralpost-schedule-tab">
      <p className="text-slate-600">Recommended post times (timezone: <b className="text-slate-800">{data.timezone}</b>). Industry benchmarks for B2B SaaS in Canada — your audience will skew similar.</p>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PLATFORMS.map((p) => {
          const h = data.hints?.[p.key];
          if (!h) return null;
          return (
            <div key={p.key} data-testid={`viralpost-schedule-card-${p.key}`} className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-700"/>
                <span className="font-bold text-slate-900">{p.label}</span>
              </div>
              <div className="mt-3 space-y-1.5 text-sm">
                <div><span className="text-slate-500">Best days:</span> <b className="text-slate-900">{h.best_days.join(", ")}</b></div>
                <div><span className="text-slate-500">Best times:</span> <b className="text-slate-900">{h.best_times.join(" · ")}</b> EST</div>
              </div>
              <p className="mt-3 text-xs text-slate-600 leading-relaxed">{h.note}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RECIPROCITY tab — daily rotating accounts to engage with
// ---------------------------------------------------------------------------
function ReciprocityTab({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reseeding, setReseeding] = useState(false);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const d = await api("/reciprocity/today", { token });
      setItems(d.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [token]);

  async function reseed() {
    if (!window.confirm("Re-generate the entire reciprocity pool? This costs one Claude call.")) return;
    setReseeding(true);
    setError("");
    try {
      await api("/reciprocity/reseed", { token, method: "POST" });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setReseeding(false);
    }
  }

  const filtered = filter === "all" ? items : items.filter((i) => (i.platform || "").includes(filter));

  return (
    <div data-testid="viralpost-reciprocity-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-slate-600 max-w-2xl">
          Today's slice from the master pool. Engage genuinely (like → comment → share) with 3-5 of these per day — they reciprocate, your reach compounds.
        </p>
        <button
          data-testid="viralpost-reseed-btn"
          onClick={reseed}
          disabled={reseeding}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-60"
        >
          {reseeding ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Shuffle className="w-3.5 h-3.5"/>}
          Reseed pool
        </button>
      </div>

      <div className="mt-5 flex items-center gap-2 flex-wrap">
        {["all", "linkedin", "x", "instagram"].map((k) => (
          <button
            key={k}
            data-testid={`viralpost-reciprocity-filter-${k}`}
            onClick={() => setFilter(k)}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors",
              filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 hover:text-slate-900 border-slate-200"
            )}
          >
            {k === "all" ? "All" : k}
          </button>
        ))}
      </div>

      {error && <div className="mt-4 px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg">{error}</div>}

      <div className="mt-5 grid md:grid-cols-2 gap-3">
        {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading today's accounts…</div>}
        {!loading && filtered.length === 0 && (
          <div className="col-span-full text-sm text-slate-500">No accounts match this filter.</div>
        )}
        {filtered.map((it, i) => (
          <div key={`${it.handle}-${i}`} data-testid={`viralpost-account-${i}`} className="bg-white border border-slate-200 rounded-xl p-4 hover:border-emerald-300 transition-colors">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold text-slate-900 truncate">{it.name}</div>
                <div className="text-xs text-emerald-700 font-mono mt-0.5">{it.handle}</div>
              </div>
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full whitespace-nowrap">{it.platform}</span>
            </div>
            <div className="mt-2 text-xs text-slate-500 uppercase tracking-wider font-bold">{it.category}</div>
            <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{it.angle}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// REPLY ASSISTANT tab — paste comment, get 3 reply variants
// ---------------------------------------------------------------------------
function ReplyAssistTab({ token }) {
  const [comment, setComment] = useState("");
  const [platform, setPlatform] = useState("linkedin");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  async function generate() {
    if (!comment.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const d = await api("/reply-assist", {
        token,
        method: "POST",
        body: { comment: comment.trim(), platform, context: context.trim() || undefined },
      });
      setResult(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function copy(key, text) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
    });
  }

  return (
    <div data-testid="viralpost-reply-tab">
      <p className="text-slate-600">Paste a comment or DM you received → Smart Operator drafts 3 brand-safe reply variants. Pick your favourite, tweak if needed, paste it back.</p>

      <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Comment / DM you received</label>
          <textarea
            data-testid="viralpost-reply-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder="Paste the comment or DM here…"
            className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none resize-none"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Platform</label>
            <select
              data-testid="viralpost-reply-platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            >
              {PLATFORMS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Context (optional)</label>
            <input
              data-testid="viralpost-reply-context"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="What's the original post about?"
              className="mt-1 w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
          </div>
        </div>
        <button
          data-testid="viralpost-reply-generate"
          onClick={generate}
          disabled={loading || !comment.trim()}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm rounded-lg disabled:opacity-60"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin"/> Drafting…</> : <><MessageCircle className="w-4 h-4"/> Draft 3 replies</>}
        </button>

        {error && <div className="text-rose-700 bg-rose-50 border border-rose-200 px-4 py-3 rounded-lg text-sm">{error}</div>}
      </div>

      {result && (
        <div className="mt-6 space-y-3" data-testid="viralpost-reply-results">
          <div className="text-xs text-slate-500">Platform: <b className="text-slate-700">{result.platform}</b> · cap: {result.char_cap} chars</div>
          {Object.entries(result.replies).map(([tone, text]) => (
            <div key={tone} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`viralpost-reply-${tone}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">{tone}</span>
                <button
                  onClick={() => copy(tone, text)}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors",
                    copiedKey === tone ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                  )}
                  data-testid={`viralpost-reply-copy-${tone}`}
                >
                  <Copy className="w-3.5 h-3.5"/>
                  {copiedKey === tone ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="mt-2 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{text}</p>
              <div className="mt-2 text-xs text-slate-400">{text.length} chars</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const TABS = [
  { key: "queue",       label: "Queue",        icon: Sparkles },
  { key: "schedule",    label: "Schedule",     icon: Calendar },
  { key: "reciprocity", label: "Reciprocity",  icon: Users },
  { key: "reply",       label: "Reply asst",   icon: MessageCircle },
];

export default function ViralPostAdmin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [activeTab, setActiveTab] = useState("queue");

  // Queue state lives here so it persists across tab switches.
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [variantB, setVariantB] = useState(false);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");

  async function load() {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const d = await api("/queue", { token });
      setItems(d.items || []);
    } catch (e) {
      // Don't auto-clear the admin token on a single 401 — could be a
      // transient backend blip or a misconfigured endpoint. Only the
      // explicit Logout button (line ~880) should kick the user out.
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function generate() {
    if (generating) return;
    setGenerating(true);
    setError("");
    try {
      await api("/queue/generate", { token, method: "POST", body: { count: 5, variant_b: variantB } });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  }

  const counts = useMemo(() => ({
    all: items.length,
    pending: items.filter((p) => p.status === "pending").length,
    approved: items.filter((p) => p.status === "approved").length,
    rejected: items.filter((p) => p.status === "rejected").length,
  }), [items]);

  if (!token) {
    return <LockScreen onUnlock={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }}/>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-5 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-emerald-700">
              <Sparkles className="w-4 h-4"/>
              <span className="text-xs uppercase tracking-[0.2em] font-bold">Smart ViralPost</span>
            </div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-tight">
              Social engine
            </h1>
          </div>
          <button
            data-testid="viralpost-logout-btn"
            onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }}
            className="text-xs text-slate-400 hover:text-slate-700 underline-offset-2 hover:underline"
          >
            Lock
          </button>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex items-center gap-1 border-b border-slate-200 overflow-x-auto" data-testid="viralpost-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`viralpost-mainTab-${t.key}`}
                onClick={() => setActiveTab(t.key)}
                className={cn(
                  "inline-flex items-center gap-1.5 px-4 py-3 text-sm font-semibold transition-colors -mb-px border-b-2 whitespace-nowrap",
                  activeTab === t.key
                    ? "border-emerald-600 text-emerald-700"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                )}
              >
                <Icon className="w-4 h-4"/>
                {t.label}
              </button>
            );
          })}
          <a
            href="https://centralrentals.ca"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 px-3"
          >
            <ExternalLink className="w-3.5 h-3.5"/> Site
          </a>
        </div>

        {/* Tab body */}
        <div className="mt-7">
          {activeTab === "queue" && (
            <QueueTab
              token={token}
              items={items}
              loading={loading}
              generating={generating}
              variantB={variantB}
              setVariantB={setVariantB}
              onGenerate={generate}
              onChange={load}
              filter={filter}
              setFilter={setFilter}
              counts={counts}
              error={error}
            />
          )}
          {activeTab === "schedule"    && <ScheduleTab    token={token} />}
          {activeTab === "reciprocity" && <ReciprocityTab token={token} />}
          {activeTab === "reply"       && <ReplyAssistTab token={token} />}
        </div>
      </div>
    </div>
  );
}
